import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Icon, Logo } from "../components/Icon";
import { ImageEditor } from "../components/ImageEditor";
import { Page, TopBar } from "../components/Layout";
import { AUDIENCES, AudienceToggle, CONTACT_KINDS, ContactLinks, ProfileHeader, contactText } from "../components/ProfileParts";
import { QR } from "../components/QR";
import { Sheet } from "../components/Sheet";
import { api, apiBlob, shellUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { normalizeSource, render, type ImageKind } from "../lib/image";
import { shareLink } from "../lib/platform";
import { useToast } from "../lib/toast";
import { versionLabel } from "../version";
import type { Audience, Contact, ContactKind, Crop, PublicProfile as PP, User } from "../lib/types";

const VISIBILITY: { value: User["profile_visibility"]; label: string; hint: string }[] = [
  { value: "private", label: "Only me", hint: "Nobody else can open your profile" },
  { value: "members", label: "Members", hint: "People in your Omni organisation" },
  { value: "public", label: "Anyone", hint: "Anyone with the link" },
];

const profileUrl = (handle: string) => `${shellUrl()}/u/${handle}`;
const IMAGE_NAME: Record<ImageKind, string> = { avatar: "Profile photo", cover: "Background" };

export function Profile() {
  const { user, setUser, logout } = useAuth();
  const toast = useToast();
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [omniPlan, setOmniPlan] = useState<string | null>(null);
  useEffect(() => {
    api<{ current: { name: string } | null }>("/api/omni/plans").then((o) => setOmniPlan(o.current?.name ?? "Free")).catch(() => undefined);
  }, []);
  if (!user) return null;
  const url = profileUrl(user.handle);

  const setVisibility = async (v: User["profile_visibility"]) => {
    setUser(await api<User>("/api/me", { method: "PATCH", body: { profile_visibility: v } }));
  };
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast("Profile link copied");
  };
  const share = async () => {
    if (!(await shareLink({ title: `${user.display_name} on Omni`, url }))) toast("Profile link copied");
  };

  return (
    <>
      <TopBar title="Profile" avatar={false}>
        <button className="link-btn" onClick={() => setEditing(true)}>Edit</button>
      </TopBar>
      <Page>
        <ProfileImages user={user} setUser={setUser}>
          {(edit) => (
            <ProfileHeader name={user.display_name} handle={user.handle} avatarUrl={user.avatar_url} coverUrl={user.cover_url}
              onEditAvatar={() => edit("avatar")} onEditCover={() => edit("cover")}>
              {user.bio ? <p style={{ fontSize: 14, color: "var(--omni-text-2)" }}>{user.bio}</p> : (
                <button className="link-btn small" style={{ alignSelf: "flex-start", minHeight: 0, padding: 0 }} onClick={() => setEditing(true)}>Add a short bio</button>
              )}
              <div className="link-box">
                <span className="m">{url.replace(/^https?:\/\//, "")}</span>
                <button className="btn btn-sm" aria-label="Copy profile link" onClick={copy}><Icon name="copy" size={20} /></button>
              </div>
              <div className="grid-2">
                <button className="btn btn-primary" onClick={share} disabled={user.profile_visibility === "private"}><Icon name="share" size={20} />Share</button>
                <button className="btn" onClick={() => setShowQR(true)} disabled={user.profile_visibility === "private"}><Icon name="qr" size={20} />QR code</button>
              </div>
            </ProfileHeader>
          )}
        </ProfileImages>

        <ContactsEditor user={user} setUser={setUser} />

        <span className="lbl">Who can see your profile</span>
        <div className="card flush" role="radiogroup" aria-label="Profile visibility">
          {VISIBILITY.map((v) => (
            <label key={v.value} className="list-row" style={{ cursor: "pointer" }}>
              <span className="grow">{v.label}<span className="sub">{v.hint}</span></span>
              <input type="radio" name="vis" style={{ width: 20, height: 20, accentColor: "var(--omni-accent)" }} checked={user.profile_visibility === v.value} onChange={() => setVisibility(v.value)} />
            </label>
          ))}
        </div>

        <span className="lbl">Account</span>
        <div className="card flush">
          <MenuRow to="/people" icon="people" label="Friends and invites" />
          <MenuRow to="/settings/notifications" icon="bell" label="Notifications" />
          <MenuRow to="/marketplace/omni" icon="sparkle" label="Omni plan" hint={omniPlan ?? undefined} />
          <MenuRow to="/settings/subscriptions" icon="card" label="Subscriptions" />
          <MenuRow to="/settings/ai" icon="gauge" label="AI usage" />
          <MenuRow to="/settings/appearance" icon="sun" label="Appearance" />
          <MenuRow to="/settings/security" icon="lock" label="Password" />
          <MenuRow to="/settings/data" icon="document" label="Your data" />
          <MenuRow to="/settings/device" icon="phone" label="On this device" />
          <MenuRow to="/settings/app" icon="download" label="Omni app and updates" />
          <MenuRow to="/settings/developer" icon="code" label="Developer" />
          <button className="list-row danger" onClick={async () => { await logout(); nav("/"); }}>
            <Icon name="logout" size={20} />Sign out
          </button>
        </div>
        <Link to={`/u/${user.handle}`} className="small center">See your profile as others do</Link>
        <p className="xs muted center m">Omni {versionLabel()}</p>
      </Page>

      {editing && <EditProfile user={user} onClose={() => setEditing(false)} onSaved={(u) => { setUser(u); setEditing(false); toast("Profile saved", "success"); }} />}
      {showQR && (
        <Sheet label="Profile QR code" onClose={() => setShowQR(false)}>
          <h2 className="center">Scan to open my profile</h2>
          <QR value={url} />
          <p className="m small center muted">{url.replace(/^https?:\/\//, "")}</p>
        </Sheet>
      )}
    </>
  );
}

export function MenuRow({ to, icon, label, hint }: { to: string; icon: string; label: string; hint?: string }) {
  return (
    <Link className="list-row" to={to}>
      <Icon name={icon} size={20} style={{ color: "var(--omni-text-muted)" }} />
      <span className="grow">{label}{hint && <span className="sub">{hint}</span>}</span>
      <Icon name="chevron-right" size={20} className="trail" />
    </Link>
  );
}

/* --------------------------------------------------------------- images */

/** Upload → edit (crop, move, zoom, rotate, adjust) → save, for the photo and
 *  the background. Re-editing starts from the kept original and last settings. */
function ProfileImages({ user, setUser, children }: { user: User; setUser: (u: User) => void; children: (edit: (k: ImageKind) => void) => JSX.Element }) {
  const toast = useToast();
  const [menu, setMenu] = useState<ImageKind | null>(null);
  const [editor, setEditor] = useState<{ kind: ImageKind; src: string; crop: Crop | null; source: Blob | null } | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const picking = useRef<ImageKind>("avatar");
  const current = (k: ImageKind) => (k === "avatar" ? user.avatar_url : user.cover_url);
  const hasSource = (k: ImageKind) => (k === "avatar" ? user.has_avatar_source : user.has_cover_source);

  const pick = (k: ImageKind) => {
    picking.current = k;
    setMenu(null);
    picker.current?.click();
  };
  const open = (k: ImageKind) => (current(k) ? setMenu(k) : pick(k));
  const close = useCallback(() => {
    setEditor((e) => {
      if (e) URL.revokeObjectURL(e.src);
      return null;
    });
  }, []);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const { blob, url } = await normalizeSource(file);
      setEditor({ kind: picking.current, src: url, crop: null, source: blob });
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  const reedit = async (k: ImageKind) => {
    setMenu(null);
    try {
      const blob = await apiBlob(`/api/me/${k}/source`);
      setEditor({ kind: k, src: URL.createObjectURL(blob), crop: k === "avatar" ? user.avatar_crop : user.cover_crop, source: null });
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  const remove = async (k: ImageKind) => {
    setMenu(null);
    setUser(await api<User>(`/api/me/${k}`, { method: "DELETE" }));
    toast(`${IMAGE_NAME[k]} removed`);
  };
  const save = async (crop: Crop, img: HTMLImageElement) => {
    if (!editor) return;
    const form = new FormData();
    form.append("image", await render(img, editor.kind, crop), `${editor.kind}.jpg`);
    if (editor.source) form.append("source", editor.source, `${editor.kind}-original.jpg`);
    form.append("crop", JSON.stringify(crop));
    setUser(await api<User>(`/api/me/${editor.kind}`, { method: "PUT", body: form }));
    toast(`${IMAGE_NAME[editor.kind]} saved`, "success");
    close();
  };

  return (
    <>
      {children(open)}
      <input ref={picker} type="file" accept="image/*" hidden onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
      {menu && (
        <Sheet label={IMAGE_NAME[menu]} onClose={() => setMenu(null)}>
          <h2>{IMAGE_NAME[menu]}</h2>
          <div className="card flush">
            <button className="list-row" onClick={() => pick(menu)}><Icon name="photo" size={20} /><span className="grow">Upload a new {menu === "avatar" ? "photo" : "background"}</span></button>
            {hasSource(menu) && <button className="list-row" onClick={() => reedit(menu)}><Icon name="pencil" size={20} /><span className="grow">Edit, crop or move<span className="sub">Starts from your original</span></span></button>}
            <button className="list-row danger" onClick={() => remove(menu)}><Icon name="trash" size={20} /><span className="grow">Remove</span></button>
          </div>
        </Sheet>
      )}
      {editor && <ImageEditor kind={editor.kind} src={editor.src} initial={editor.crop} onCancel={close} onSave={save} />}
    </>
  );
}

/* ------------------------------------------------------------- contacts */

const toBody = (list: Contact[]) => list.map(({ id, kind, value, label, visibility }) => ({ id, kind, value, label, visibility }));

function ContactsEditor({ user, setUser }: { user: User; setUser: (u: User) => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState<Contact | "new" | null>(null);
  const [contacts, setContacts] = useState(user.contacts);
  useEffect(() => setContacts(user.contacts), [user.contacts]);

  const saveAll = async (list: Contact[]) => {
    const prev = contacts;
    setContacts(list);
    try {
      const u = await api<User>("/api/me", { method: "PATCH", body: { contacts: toBody(list) } });
      setUser(u);
    } catch (e) {
      setContacts(prev);
      throw e;
    }
  };
  const setAudience = (c: Contact, v: Audience) =>
    saveAll(contacts.map((x) => (x.id === c.id ? { ...x, visibility: v } : x))).catch((e) => toast((e as Error).message, "error"));

  return (
    <>
      <div className="row between">
        <span className="lbl">Contact info</span>
        <span className="xs muted row" style={{ gap: 10 }}>
          {(Object.keys(AUDIENCES) as Audience[]).map((a) => <span key={a} className="row" style={{ gap: 3 }}><Icon name={AUDIENCES[a].icon} size={14} />{AUDIENCES[a].label}</span>)}
        </span>
      </div>
      <div className="card flush">
        {contacts.map((c) => (
          <div key={c.id} className="list-row" role="button" tabIndex={0} onClick={() => setEditing(c)} onKeyDown={(e) => e.key === "Enter" && setEditing(c)} style={{ cursor: "pointer" }}>
            <span className="contact-ic"><Icon name={CONTACT_KINDS[c.kind].icon} size={20} /></span>
            <span className="grow" style={{ minWidth: 0 }}>
              <span className="ellipsis">{contactText(c)}</span>
              <span className="sub">{CONTACT_KINDS[c.kind].name}</span>
            </span>
            <AudienceToggle value={c.visibility} onChange={(v) => setAudience(c, v)} />
          </div>
        ))}
        <button className="list-row" onClick={() => setEditing("new")}>
          <span className="contact-ic add"><Icon name="plus" size={20} /></span>
          <span className="grow">{contacts.length ? "Add contact" : "Add email, phone or social profiles"}<span className="sub">Choose who sees each one</span></span>
        </button>
      </div>
      {editing && (
        <ContactSheet
          contact={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={async (c) => {
            await saveAll(editing === "new" ? [...contacts, c] : contacts.map((x) => (x.id === c.id ? c : x)));
            setEditing(null);
          }}
          onDelete={editing === "new" ? undefined : async () => {
            await saveAll(contacts.filter((x) => x.id !== (editing as Contact).id));
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function ContactSheet({ contact, onClose, onSave, onDelete }: { contact: Contact | null; onClose: () => void; onSave: (c: Contact) => Promise<void>; onDelete?: () => Promise<void> }) {
  const [c, setC] = useState<Contact>(contact ?? { id: "", kind: "email", value: "", label: "", visibility: "friends" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const meta = CONTACT_KINDS[c.kind];
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet label={contact ? "Edit contact" : "Add contact"} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); run(() => onSave({ ...c, value: c.value.trim() })); }}>
        <h2>{contact ? "Edit contact" : "Add contact"}</h2>
        <div className="kind-grid" role="radiogroup" aria-label="Type">
          {(Object.keys(CONTACT_KINDS) as ContactKind[]).map((k) => (
            <button type="button" key={k} role="radio" aria-checked={c.kind === k} className={`kind-btn${c.kind === k ? " on" : ""}`} onClick={() => setC({ ...c, kind: k })}>
              <Icon name={CONTACT_KINDS[k].icon} size={20} />
              <span>{CONTACT_KINDS[k].name}</span>
            </button>
          ))}
        </div>
        <label className="field">
          <span className="field-label">{meta.name}</span>
          <input className="input" autoFocus required type={meta.input === "url" ? "text" : meta.input} inputMode={meta.input === "url" ? "url" : undefined}
            autoCapitalize="off" autoCorrect="off" placeholder={meta.placeholder} maxLength={300} value={c.value} onChange={(e) => setC({ ...c, value: e.target.value })} />
        </label>
        {(c.kind === "website" || c.kind === "other") && (
          <label className="field"><span className="field-label">Label (optional)</span><input className="input" maxLength={40} placeholder="Portfolio" value={c.label} onChange={(e) => setC({ ...c, label: e.target.value })} /></label>
        )}
        <div className="row between">
          <span className="stack" style={{ gap: 0 }}>
            <span className="field-label">Who can see it</span>
            <span className="xs muted">{AUDIENCES[c.visibility].hint}</span>
          </span>
          <AudienceToggle value={c.visibility} onChange={(v) => setC({ ...c, visibility: v })} />
        </div>
        {error && <p className="error small" role="alert">{error}</p>}
        <button className="btn btn-primary btn-block" disabled={busy}>{contact ? "Save" : "Add"}</button>
        {onDelete && <button type="button" className="btn btn-block btn-ghost danger" disabled={busy} onClick={() => run(onDelete)}><Icon name="trash" size={18} />Remove</button>}
      </form>
    </Sheet>
  );
}

/* ---------------------------------------------------------- edit details */

function EditProfile({ user, onClose, onSaved }: { user: User; onClose: () => void; onSaved: (u: User) => void }) {
  const [form, setForm] = useState(user);
  const [error, setError] = useState<string | null>(null);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      onSaved(await api<User>("/api/me", { method: "PATCH", body: { display_name: form.display_name, handle: form.handle, bio: form.bio, currency: form.currency } }));
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <Sheet label="Edit profile" onClose={onClose}>
      <form className="stack" onSubmit={save}>
        <h2>Edit profile</h2>
        <label className="field"><span className="field-label">Name</span><input className="input" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></label>
        <label className="field"><span className="field-label">Handle</span><input className="input m" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value.toLowerCase() })} /></label>
        <label className="field"><span className="field-label">Bio</span><textarea className="input" rows={3} maxLength={1000} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></label>
        <label className="field"><span className="field-label">Currency apps should use</span><input className="input m" maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></label>
        <p className="xs muted">Photo, background and contact details are edited on the profile itself.</p>
        {error && <p className="error small" role="alert">{error}</p>}
        <button className="btn btn-primary btn-block">Save profile</button>
      </form>
    </Sheet>
  );
}

/* ------------------------------------------------------- public profile */

type Viewer = "anyone" | "friend" | "me";
const PREVIEW: Record<Viewer, { label: string; sees: Audience[] }> = {
  anyone: { label: "Anyone", sees: ["public"] },
  friend: { label: "A friend", sees: ["public", "friends"] },
  me: { label: "You", sees: ["public", "friends", "private"] },
};

export function PublicProfile() {
  const { handle } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const [p, setP] = useState<PP | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Viewer>("anyone");
  const load = useCallback(
    () => api<PP>(`/api/profiles/${handle}`).then((r) => setP({ ...r, contacts: r.contacts ?? [], cover_url: r.cover_url ?? "", connection: r.connection ?? null })).catch((e) => setError(e.message)),
    [handle],
  );
  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast(done, "success");
      load();
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  const share = async () => {
    if (p && !(await shareLink({ title: `${p.display_name} on Omni`, url: profileUrl(p.handle) }))) toast("Profile link copied");
  };
  const contacts = p ? (p.is_owner ? p.contacts.filter((c) => PREVIEW[preview].sees.includes(c.visibility)) : p.contacts) : [];

  return (
    <div className="public-profile">
      <div className="wordmark"><Logo size={26} />omni</div>
      {error && <p className="card muted center">{error}</p>}
      {p && (
        <>
          {p.is_owner && (
            <div className="card stack" style={{ gap: 8 }}>
              <span className="small b">Preview as</span>
              <div className="chips" role="radiogroup" aria-label="Preview as" style={{ margin: 0, padding: 0 }}>
                {(Object.keys(PREVIEW) as Viewer[]).map((v) => (
                  <button key={v} role="radio" aria-checked={preview === v} className={`chip${preview === v ? " on" : ""}`} onClick={() => setPreview(v)}>{PREVIEW[v].label}</button>
                ))}
              </div>
            </div>
          )}
          <ProfileHeader name={p.display_name} handle={p.handle} avatarUrl={p.avatar_url} coverUrl={p.cover_url}>
            {p.bio && <p style={{ fontSize: 15 }}>{p.bio}</p>}
            <p className="xs muted">On Omni since {new Date(p.member_since).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
            <div className="row wrap">
              {p.can_connect && <FriendButton p={p} act={act} />}
              {p.is_owner && <Link className="btn" to="/profile"><Icon name="pencil" size={18} />Edit profile</Link>}
              <button className="btn" onClick={share}><Icon name="share" size={18} />Share</button>
            </div>
          </ProfileHeader>
          {contacts.length > 0 && (
            <>
              <span className="lbl">Contact</span>
              <ContactLinks contacts={contacts} showAudience={p.is_owner} />
            </>
          )}
        </>
      )}
      {!user && <Link className="btn btn-primary btn-block" to="/register">Join Omni</Link>}
      {user && !p?.is_owner && <Link className="btn btn-block btn-ghost" to="/">Back to my space</Link>}
    </div>
  );
}

function FriendButton({ p, act }: { p: PP; act: (fn: () => Promise<unknown>, done: string) => void }) {
  const c = p.connection;
  if (!c) return <button className="btn btn-primary" onClick={() => act(() => api("/api/connections", { body: { handle: p.handle } }), "Friend request sent")}><Icon name="user-plus" size={18} />Add friend</button>;
  if (c.status === "accepted")
    return <button className="btn" onClick={() => confirm(`Remove ${p.display_name} from your friends?`) && act(() => api(`/api/connections/${c.id}`, { method: "DELETE" }), "Removed from friends")}><Icon name="user-check" size={18} />Friends</button>;
  if (c.direction === "incoming")
    return (
      <>
        <button className="btn btn-primary" onClick={() => act(() => api(`/api/connections/${c.id}/accept`, { method: "POST" }), "You're now friends")}><Icon name="user-check" size={18} />Accept friend request</button>
        <button className="btn btn-ghost" onClick={() => act(() => api(`/api/connections/${c.id}`, { method: "DELETE" }), "Request declined")}>Decline</button>
      </>
    );
  return <button className="btn" onClick={() => act(() => api(`/api/connections/${c.id}`, { method: "DELETE" }), "Request cancelled")}><Icon name="user-plus" size={18} />Requested · cancel</button>;
}
